import React from 'react'
import {
  faBatteryFull, faBolt, faListCheck, faPlug, faWallet,
} from '@fortawesome/free-solid-svg-icons'
import useDocumentTitle from '../hooks/useDocumentTitle'
import KreatorSzkielet from '../components/kreator/KreatorSzkielet'
import { dobierzFalownik } from '../api/falowniki'

/**
 * Asystent doboru falownika. Pytania o DOM, nie o produkt: przekład na parametry
 * robi backend (`backend_api/dobor_falownika.py`).
 *
 * ═══ CZEGO TU NIE MA ═══
 * Nie pytamy o pompę ciepła ani SG Ready. Zmierzone 2026-08-20: dane mamy dla
 * 38% zbadanych serii, czyli 13% ofert. Pytanie, na które w większości
 * przypadków odpowiedzielibyśmy „nie wiemy", jest gorsze niż jego brak.
 */

const PYTANIA = [
  {
    klucz: 'fazy', ikona: faPlug, etykieta: 'Fazy',
    tytul: 'Ile faz masz w domu?',
    opis: 'Widać to na liczniku albo w umowie z operatorem. Falownik 3-fazowy nie zadziała na instalacji 1-fazowej.',
    opcje: [
      { v: '1', l: '1 faza', o: 'Zwykle starsze domy i mieszkania' },
      { v: '3', l: '3 fazy', o: 'Standard w nowych domach' },
      { v: '', l: 'Nie wiem', o: 'Pokażemy sprzęt z obu grup' },
    ],
  },
  {
    klucz: 'magazyn', ikona: faBatteryFull, etykieta: 'Magazyn',
    tytul: 'Masz albo planujesz magazyn energii?',
    opis: 'Magazyn wymaga falownika hybrydowego. Jeśli planujesz go dopiero za jakiś czas, i tak warto od razu kupić hybrydę, bo dokładanie jej później oznacza wymianę falownika.',
    opcje: [
      { v: 'mam', l: 'Mam już magazyn', o: 'Szukamy wyłącznie hybryd' },
      { v: 'planuje', l: 'Planuję', o: 'Szukamy wyłącznie hybryd' },
      { v: 'nie', l: 'Nie planuję', o: 'W pierwszej kolejności falowniki sieciowe' },
    ],
  },
  {
    klucz: 'zaniki_pradu', ikona: faBolt, etykieta: 'Zaniki prądu',
    tytul: 'Jak często masz przerwy w dostawie prądu?',
    opis: 'W przypadku częstych przerw rekomendujemy falowniki z zaawansowanymi możliwościami konfiguracji zasilania awaryjnego.',
    warunek: (o) => o.magazyn === 'mam' || o.magazyn === 'planuje',
    opcje: [
      { v: 'czesto', l: 'Często', o: 'Zasilanie awaryjne będzie priorytetem' },
      { v: 'rzadko', l: 'Rzadko', o: 'Potraktujemy je jako plus' },
      { v: 'prawie_nigdy', l: 'Prawie nigdy', o: 'Nie będziemy tego punktować' },
    ],
  },
  {
    klucz: 'taryfa_dynamiczna', ikona: faWallet, etykieta: 'Taryfa',
    tytul: 'Masz albo planujesz taryfę z cenami godzinowymi?',
    opis: 'Przy takiej taryfie liczy się możliwość zaplanowania, kiedy magazyn się ładuje.',
    warunek: (o) => o.magazyn !== 'nie',
    opcje: [
      { v: 'tak', l: 'Tak', o: 'Punktujemy harmonogramy i sterowanie' },
      { v: 'nie', l: 'Nie', o: '' },
      { v: 'nie_wiem', l: 'Jeszcze nie wiem', o: '' },
    ],
  },
  {
    klucz: 'priorytet', ikona: faListCheck, etykieta: 'Priorytet',
    tytul: 'Co jest dla Ciebie ważniejsze?', opis: '',
    // Priorytet zmienia kolejność i dobór w backendzie (`dobor_falownika.WAGI_PRIORYTETU`, błąd I3 2026-10-26).
    opcje: [
      { v: 'ekonomia', l: 'Najniższa cena', o: 'Najtańsze spełniające Twoje wymagania' },
      { v: 'balans', l: 'Rozsądny balans', o: 'Cena i możliwości po równo' },
      { v: 'jakosc', l: 'Możliwości sprzętu', o: 'Liczba MPPT, gwarancja, praca bez sieci' },
    ],
  },
]

// Wiersze z danych badawczych. Świadomie wąski zestaw: wartości wielozdaniowe
// (rodzaj backupu, wymagany osprzęt, ekosystem magazynów) rozpychały tabelę na
// wysokość ekranu, a przy wyborze rozstrzygają rzadko. Zostają w pełnym porównaniu.
const WIERSZE_BADAWCZE = [
  ['backup_czy_jest', 'Zasilanie awaryjne'],
  ['backup_czas_przelaczenia_ms', 'Czas przełączenia'],
  ['praca_wyspowa_offgrid', 'Praca bez sieci'],
  ['harmonogram_ladowania', 'Harmonogram ładowania'],
  ['serwis_pl_lokalizacja', 'Oficjalny serwis w Polsce'],
  ['gwarancja_lata', 'Gwarancja producenta'],
  ['gwarancja_przedluzenie', 'Przedłużenie gwarancji'],
  ['aplikacja_mobilna', 'Aplikacja'],
]

// Sama nazwa miasta nic nie mówi komuś, kto wybiera falownik: pytanie brzmi
// „czy mam się do kogo zgłosić w kraju", a nie „gdzie dokładnie".
const SKROTY = { serwis_pl_lokalizacja: (v) => (/brak/i.test(v) ? 'Nie' : 'Tak') }

// `osadzony`: strona w oknie nad Projektem 3D (uwaga F7, 2026-10-25): odpowiedzi startowe z modelu, bez zmiany tytułu
// karty, a „Użyj w projekcie” przy propozycji oddaje ją do `onUzyjModelu`.
const DoborFalownika = ({ osadzony = false, odpowiedziStartowe = null, onUzyjModelu = null }) => {
  useDocumentTitle(osadzony ? 'Projekt 3D' : 'Asystent doboru falownika')
  return (
    <KreatorSzkielet
      tytul="Asystent doboru falownika"
      opis="Odpowiedz na kilka pytań o swój dom. Przejrzymy cały katalog i pokażemy trzy propozycje z uzasadnieniem, dlaczego akurat te."
      pytania={PYTANIA}
      domyslne={osadzony ? odpowiedziStartowe || {} : {}}
      tekstPrzycisku="Pokaż dopasowane falowniki"
      onDobierz={(o) => dobierzFalownik({ priorytet: 'balans', ...o })}
      konfiguracjaWynikow={() => ({
        kategoria: 'inwerter',
        wiersze: [
          ['Moc nominalna', (p) => (p.moc_kw ? `${p.moc_kw} kW` : null)],
          ['Typ falownika', (p) => p.typ],
          ['Fazy', (p) => p.fazy],
          ['Liczba MPPT', (p) => (p.mppt != null ? String(p.mppt) : null)],
          ['Liczba ofert', (p) => `${p.ofert}`],
        ],
        wierszeBadawcze: WIERSZE_BADAWCZE,
        skroty: SKROTY,
        cta: (p) => `Zobacz ${p.ofert} ofert`,
        ...(onUzyjModelu ? { onUzyj: onUzyjModelu } : {}),
        stopka: 'Ocena liczy się z Twoich odpowiedzi i danych, które mamy o tych modelach. Nie ocenia jakości wykonania ani awaryjności, bo takich danych w katalogu nie ma. Ikona informacji przy wartości prowadzi do źródła.',
      })}
    />
  )
}

export default DoborFalownika
